import React, { useState, useEffect } from 'react';
import { ThemePrimaryButton, TextCancelButton } from '../button';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Typography, 
  Box, 
  Divider,
  Paper,
  CircularProgress
} from '@mui/material';
import DoneIcon from '@mui/icons-material/Done';
import GavelIcon from '@mui/icons-material/Gavel';

const TermsAgreementDialog = ({ open, onClose, onSign, userProfile, billingInfo }) => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [termsContent, setTermsContent] = useState(null);
  
  useEffect(() => {
    if (open) {
      // Fetch terms content if needed
      // For demo purposes, we'll just use a static content set in state
      setTermsContent(`
      <h2>Terms of Service</h2>
      <p>By using Mailsball's email and SMS marketing services, you agree to comply with the following terms:</p>
      
      <h3>Prohibited Uses</h3>
      <ul>
        <li>Sending spam, unsolicited email, or unsolicited SMS to recipients without valid consent or another lawful basis</li>
        <li>Using purchased, scraped, or rented contact lists without documented opt-in consent</li>
        <li>Engaging in fraudulent activities, phishing, spoofing, or deceptive practices</li>
        <li>Violating applicable laws including GDPR, ePrivacy rules, CAN-SPAM, CASL, TCPA/CTIA, and local telecommunications regulations</li>
        <li>Failing to honour STOP/unsubscribe requests for SMS or email marketing</li>
        <li>Distributing malware, viruses, or malicious content</li>
      </ul>
      
      <h3>User Responsibility</h3>
      <p>You assume full responsibility for all content and recipient lists transmitted through our service. You bear sole responsibility for all email and SMS campaigns, message content, consent records, and compliance with laws in each destination country.</p>
      
      <h3>SMS Credits</h3>
      <p>SMS campaigns are billed via prepaid credits unless your plan includes unlimited SMS. Credits are generally non-refundable. Delivery is subject to carrier and provider rules.</p>
      
      <h3>Account Suspension</h3>
      <p>We reserve the right to suspend or terminate your account without notice if we believe you have violated these terms or carrier policies.</p>
      
      <h3>Disclaimer of Liability</h3>
      <p>Mailsball expressly disclaims all liability for actions, content, or campaigns distributed by users through our service. We provide the platform as a technical intermediary only and do not guarantee email or SMS deliverability.</p>
      `);
    }
  }, [open]);

  const handleSign = async () => {
    setLoading(true);
    try {
      // Просто показываем успешное подтверждение, без вызова API
      setTimeout(() => {
        setSuccess(true);
        
        // После успеха закрываем диалог
        setTimeout(() => {
          setSuccess(false);
          setLoading(false);
          onSign(); // Вызываем callback для обновления состояния в родительском компоненте
        }, 1500);
      }, 500);
    } catch (error) {
      console.error('Error signing agreement:', error);
      setLoading(false);
    }
  };

  // Format date as ISO string but only the date part
  const today = new Date().toISOString().split('T')[0];

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="terms-dialog-title"
    >
      <DialogTitle id="terms-dialog-title" sx={{ display: 'flex', alignItems: 'center' }}>
        <GavelIcon sx={{ mr: 1 }} />
        Terms of Service Agreement
      </DialogTitle>
      
      <DialogContent dividers>
        {!termsContent ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 2, 
                mb: 3, 
                maxHeight: '350px', 
                overflow: 'auto',
                bgcolor: '#f9f9f9',
                border: '1px solid #e0e0e0'
              }}
            >
              <div dangerouslySetInnerHTML={{ __html: termsContent }} />
            </Paper>
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              By signing this agreement, you confirm that:
            </Typography>
            
            <Box component="ul" sx={{ pl: 2, mb: 3 }}>
              <li>
                <Typography paragraph>
                  You have read, understood, and agree to comply with the Terms of Service.
                </Typography>
              </li>
              <li>
                <Typography paragraph>
                  You take full responsibility for all email and SMS content and recipient lists sent through the Mailsball platform.
                </Typography>
              </li>
              <li>
                <Typography paragraph>
                  You will comply with all applicable laws and regulations related to email and SMS marketing, including consent and opt-out requirements.
                </Typography>
              </li>
              <li>
                <Typography paragraph>
                  You acknowledge that Mailsball may terminate your account for violations of these terms.
                </Typography>
              </li>
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="h6" gutterBottom>
              User Information
            </Typography>
            
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Full Name
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {userProfile?.firstName} {userProfile?.lastName}
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Login
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {userProfile?.login}
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Email Address
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {userProfile?.email}
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Date
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {today}
                </Typography>
              </Box>
            </Box>
            
            {billingInfo && (
              <>
                <Typography variant="h6" gutterBottom>
                  Billing Information
                </Typography>
                
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  {billingInfo.company && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Company
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {billingInfo.company}
                      </Typography>
                    </Box>
                  )}
                  
                  {billingInfo.address && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Address
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {billingInfo.address}
                      </Typography>
                    </Box>
                  )}
                  
                  {billingInfo.country && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Country
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {billingInfo.country}
                      </Typography>
                    </Box>
                  )}
                  
                  {billingInfo.city && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        City
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {billingInfo.city}
                      </Typography>
                    </Box>
                  )}
                  
                  {billingInfo.postalCode && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Postal Code
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {billingInfo.postalCode}
                      </Typography>
                    </Box>
                  )}
                  
                  {billingInfo.vat && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        VAT
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {billingInfo.vat}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </>
            )}
          </>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'space-between' }}>
        <TextCancelButton onClick={onClose}>
          Cancel
        </TextCancelButton>
        <ThemePrimaryButton 
          onClick={handleSign} 
          disabled={loading || success}
          sx={{ 
            minWidth: '120px', 
            position: 'relative' 
          }}
        >
          {loading && <CircularProgress size={24} sx={{ position: 'absolute', left: '50%', top: '50%', marginLeft: '-12px', marginTop: '-12px' }} />}
          {success ? <DoneIcon sx={{ mr: 1 }} /> : null}
          {success ? 'Signed' : 'Sign Agreement'}
        </ThemePrimaryButton>
      </DialogActions>
    </Dialog>
  );
};

export default TermsAgreementDialog; 